<template>
  <div class="flex-shrink-0 border-b p-4">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-semibold text-gray-800">
        {{
          bond.bondType === 'MSB' && bond.alias
            ? bond.alias
            : formatBondName(bond.bondName)
        }}
        <span class="text-gray-500 text-sm ml-2">({{ bond.bondName }})</span>
      </h3>
      <button @click="copyToClipboard(bond.code)"
        class="flex items-center text-gray-500 hover:text-blue-500 px-3 py-1 rounded-md border border-gray-200 hover:border-blue-300 text-sm transition-colors">
        <span class="font-mono">{{ bond.code }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </div>

    <!-- 주요 정보 요약 -->
    <div class="bg-gray-50 p-3 rounded mb-4">
      <div class="flex items-center text-sm text-gray-600 space-x-4">
        <div class="flex items-center">
          <span class="text-gray-500 mr-1">민평:</span>
          <span class="font-mono">{{ bond.yield || '-' }}</span>
        </div>
        <div class="w-px h-4 bg-gray-300"></div>
        <div class="flex items-center">
          <span class="text-gray-500 mr-1">만기일:</span>
          <span>{{ bond.maturityDate || '-' }}</span>
        </div>
        <div class="w-px h-4 bg-gray-300"></div>
        <div class="flex items-center">
          <span class="text-gray-500 mr-1">잔존기간:</span>
          <span>{{ formatRemainingTerm(bond.maturityDate) }}</span>
        </div>
      </div>
    </div>

    <!-- 매도/매수 호가 정보 -->
    <div class="grid grid-cols-2 gap-6 relative">
      <!-- 중앙 구분선 -->
      <div class="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 transform -translate-x-1/2"></div>

      <!-- 매도 호가 -->
      <div class="p-3 text-right">
        <div class="text-xs font-medium text-gray-600 mb-1">최근 매도 호가</div>
        <div class="flex items-baseline justify-end space-x-1">
          <div class="text-xl font-mono font-semibold text-gray-800 rolling-number"
            :class="{ 'animate-roll': isAskUpdated }">
            {{ formatYieldForDisplay(bond.askYield) }}
          </div>
          <span class="text-xl font-mono font-semibold text-gray-800">-</span>
        </div>
        <div class="text-xs text-gray-400 mt-1" v-if="bond.askAt">
          {{ formatTimeDetailed(bond.askAt) }}
        </div>
      </div>

      <!-- 매수 호가 -->
      <div class="p-3 text-left">
        <div class="text-xs font-medium text-gray-600 mb-1">최근 매수 호가</div>
        <div class="flex items-baseline justify-start space-x-1">
          <div class="text-xl font-mono font-semibold text-gray-800 rolling-number"
            :class="{ 'animate-roll': isBidUpdated }">
            {{ formatYieldForDisplay(bond.bidYield) }}
          </div>
          <span class="text-xl font-mono font-semibold text-gray-800">+</span>
        </div>
        <div class="text-xs text-gray-400 mt-1" v-if="bond.bidAt">
          {{ formatTimeDetailed(bond.bidAt) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { differenceInMonths } from 'date-fns';

export default {
  name: 'BondDetailComponent',
  props: {
    bond: {
      type: Object,
      required: true,
    },
  },
  emits: ['copy-to-clipboard'],
  setup(props, { emit }) {
    // 애니메이션 상태 관리
    const isAskUpdated = ref(false);
    const isBidUpdated = ref(false);
    // 클립보드 복사 함수
    const copyToClipboard = text => {
      emit('copy-to-clipboard', text);
    };

    // 잔존만기 포맷팅 함수
    const formatRemainingTerm = maturityDate => {
      if (!maturityDate) return '-';

      try {
        const today = new Date();
        const maturity = new Date(maturityDate);

        // 전체 개월 수 계산
        const monthsDiff = differenceInMonths(maturity, today);

        if (monthsDiff < 12) {
          // 1년 미만: x개월
          return `${monthsDiff}개월`;
        } else {
          // 1년 이상: x년 y개월
          const years = Math.floor(monthsDiff / 12);
          const months = monthsDiff % 12;

          if (months === 0) {
            return `${years}년`;
          } else {
            return `${years}년 ${months}개월`;
          }
        }
      } catch (error) {
        console.error('만기일 포맷 오류:', error);
        return '없음';
      }
    };

    const formatBondName = bondName => {
      if (!bondName) return '';

      // 괄호 안의 내용 추출 (예: 국고04250-2512(22-13) -> 22-13)
      const match = bondName.match(/\(([^)]+)\)$/);
      if (match && match[1]) {
        return match[1];
      }

      // 괄호가 없는 경우 원래 문자열 반환
      return bondName;
    };

    // 수익률 표시 포매팅 (세트 거래 처리)
    const formatYieldDisplay = (yieldData) => {
      if (!yieldData || yieldData.yield === null || yieldData.yield === undefined) return '';

      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트 거래일 때: "2.410/2.415" 형식
        return `${yieldData.yield.toFixed(3)}/${yieldData.setYield.toFixed(3)}`;
      }

      // 일반 거래일 때: "2.410" 형식
      return yieldData.yield.toFixed(3);
    };

    // 새로운 수익률 표시 함수 (소수부만 표시)
    const formatYieldForDisplay = (yieldData) => {
      if (!yieldData || yieldData.yield === null || yieldData.yield === undefined) return '';

      if (yieldData.isSet && yieldData.setYield !== null) {
        // 세트 거래일 때: "420/425" 형식 (소수부만)
        const yield1 = Math.round((yieldData.yield % 1) * 1000);
        const yield2 = Math.round((yieldData.setYield % 1) * 1000);
        return `${yield1}/${yield2}`;
      }

      // 일반 거래일 때: "420" 형식 (소수부만)
      return Math.round((yieldData.yield % 1) * 1000).toString();
    };

    // 시간 포맷팅 함수 (기존)
    const formatTime = (timeString) => {
      if (!timeString) return '';

      try {
        const date = new Date(timeString);
        const now = new Date();
        const diffMs = now - date;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        if (diffMinutes < 1) {
          return '방금';
        } else if (diffMinutes < 60) {
          return `${diffMinutes}분 전`;
        } else if (diffHours < 24) {
          return `${diffHours}시간 전`;
        } else {
          // 24시간 이상이면 시:분 형식으로
          return date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      } catch (error) {
        console.error('시간 포맷 오류:', error);
        return '';
      }
    };

    // 상세 시간 포맷팅 함수 (hh:mm:ss + 상대시간)
    const formatTimeDetailed = (timeString) => {
      if (!timeString) return '';

      try {
        const date = new Date(timeString);

        // 유효한 날짜인지 확인
        if (isNaN(date.getTime())) {
          console.warn('유효하지 않은 날짜:', timeString);
          return '';
        }

        const now = new Date();
        const diffMs = now - date;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        // 항상 시:분:초 표시
        const timeStr = date.toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        // 상대시간 계산
        let relativeTime = '';
        if (diffMinutes < 1) {
          relativeTime = '(방금)';
        } else if (diffMinutes < 60) {
          relativeTime = `(${diffMinutes}분 전)`;
        } else if (diffHours < 24) {
          const remainingMinutes = diffMinutes % 60;
          if (remainingMinutes === 0) {
            relativeTime = `(${diffHours}시간 전)`;
          } else {
            relativeTime = `(${diffHours}시간 ${remainingMinutes}분 전)`;
          }
        } else {
          // 24시간 이상은 상대시간 표시 안함
          relativeTime = '';
        }

        return `${timeStr} ${relativeTime}`.trim();
      } catch (error) {
        console.error('시간 포맷 오류:', error, timeString);
        return '';
      }
    };

    // 매도 호가 업데이트 감지
    watch(() => props.bond.lastAskUpdate, (newTimestamp) => {
      if (newTimestamp) {
        isAskUpdated.value = true;
        setTimeout(() => {
          isAskUpdated.value = false;
        }, 1000); // 1초 후 애니메이션 해제
      }
    });

    // 매수 호가 업데이트 감지
    watch(() => props.bond.lastBidUpdate, (newTimestamp) => {
      if (newTimestamp) {
        isBidUpdated.value = true;
        setTimeout(() => {
          isBidUpdated.value = false;
        }, 1000); // 1초 후 애니메이션 해제
      }
    });

    return {
      copyToClipboard,
      formatRemainingTerm,
      formatBondName,
      formatYieldDisplay,
      formatYieldForDisplay,
      formatTime,
      formatTimeDetailed,
      isAskUpdated,
      isBidUpdated,
    };
  },
};
</script>

<style scoped>
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

/* 숫자 롤링 애니메이션 */
.rolling-number {
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease-in-out;
}

.animate-roll {
  animation: roll-numbers 0.6s ease-in-out;
}

@keyframes roll-numbers {
  0% {
    transform: translateY(0);
  }

  25% {
    transform: translateY(-10px);
  }

  75% {
    transform: translateY(10px);
  }

  100% {
    transform: translateY(0);
  }
}

/* 부드러운 전환 효과 */
.transition-all {
  transition: all 0.3s ease-in-out;
}
</style>
